<template>
  <InteractionStep :disabled-next="false"
                   :pending-next="pendingSave"
                   @next="onSave">

    <template v-slot:content>
      <div class="step-information">
        <div class="step-information__section _small">
          <BaseInput :label="$tInteraction('Maiden name / Last name at birth')"
                     :value="inputs.first_surname.value"
                     :error="inputs.first_surname.error"
                     @input="setInputValue(['first_surname', $event])"
                     class="step-information__input"
          />
          <div class="step-information__col-6">
            <BaseInput :label="$tInteraction('Country of birth')"
                       :value="inputs.birth_country.value"
                       :error="inputs.birth_country.error"
                       @input="setInputValue(['birth_country', $event])"
            />
          </div>
          <div class="step-information__col-6">
            <BaseInput :label="$tInteraction('Place of Birth')"
                       :value="inputs.birth_location.value"
                       :error="inputs.birth_location.error"
                       @input="setInputValue(['birth_location', $event])"
            />
          </div>
        </div>
        <div class="step-information__section _small">
          <span class="step-information__label">
            {{ $tInteraction('Civil status') }}
          </span>
          <div class="step-information__card">
            <div class="step-information__row">
              <ButtonCheckList :value="inputs.marital_status.value"
                               :error="inputs.marital_status.error"
                               :options="maritalStatuses"
                               type="radio"
                               compareType="id"
                               text-key="name"
                               @input="onMaritalStatusInput"
                               class="_cols-bigger"
              />
            </div>
            <div class="step-information__row" v-if="showMaritalStatusDescription">
              <div class="step-information__col-12">
                <BaseInput
                    :label="$tInteraction('Clarify')"
                    :value="inputs.marital_status_description.value"
                    :error="inputs.marital_status_description.error"
                    @input="setInputValue(['marital_status_description', $event])"
                    class="_white"/>
              </div>
            </div>
          </div>
        </div>
        <div class="step-information__section _small">
          <BaseInput :label="$tInteraction('INN')"
                     :value="inputs.inn.value"
                     :error="inputs.inn.error"
                     maxlength="12"
                     @input="setInputValue(['inn', $event])"
                     class="step-information__input"/>
        </div>
        <div class="step-information__section _small">
          <span class="step-information__label">
            {{ $tInteraction('International passport') }}
          </span>
          <div class="step-information__card">
            <div class="step-information__row">
              <div class="step-information__col-6">
                <DatePiker :label="$tInteraction('Date of issue')"
                           :value="inputs.international_passport_creation_date.value"
                           :error="inputs.international_passport_creation_date.error"
                           @input="setInputValue(['international_passport_creation_date', $event])"
                           class="_green _white"
                />
              </div>
              <div class="step-information__col-6">
                <DatePiker :label="$tInteraction('Expiration date')"
                           :value="inputs.international_passport_expired_date.value"
                           :error="inputs.international_passport_expired_date.error"
                           @input="setInputValue(['international_passport_expired_date', $event])"
                           class="_green _white"
                />
              </div>
            </div>
            <div class="step-information__row">
              <div class="step-information__col-12">
                <BaseInput :label="$tInteraction('Issued by')"
                           :value="inputs.international_passport_maker.value"
                           :error="inputs.international_passport_maker.error"
                           @input="setInputValue(['international_passport_maker', $event])"
                           class="step-information__input _white"/>
              </div>
            </div>
          </div>
        </div>
        <div class="step-information__section _small">
          <span class="step-information__label">
            {{
              $tInteraction('You reside permanently in a country that is not the country of issue of your citizenship document')
            }}?
          </span>
          <div class="step-information__card">
            <div class="step-information__row">
              <ButtonCheckList
                  :value="inputs.document_live_in_other_country.value"
                  :error="inputs.document_live_in_other_country.error"
                  :options="yesOrNo"
                  type="radio"
                  compareType="id"
                  text-key="name"
                  @input="onDocumentLiveInOtherCountryInput"
              />
            </div>
            <template v-if="showDocumentLiveInOtherCountry">
              <div class="step-information__row">
                <div class="step-information__col-12">
                  <BaseSelect :label="$tInteraction('Document')"
                              :value="inputs.document.value"
                              :error="inputs.document.error"
                              :options="documents"
                              text-key="name"
                              @input="setInputValue(['document', $event])"
                              class="_white"
                  />
                </div>
              </div>
              <div class="step-information__row">
                <div class="step-information__col-6">
                  <BaseInput :label="$tInteraction('Number')"
                             :value="inputs.document_number.value"
                             :error="inputs.document_number.error"
                             @input="setInputValue(['document_number', $event])"
                             class="_white"/>
                </div>
                <div class="step-information__col-6">
                  <DatePiker :label="$tInteraction('Valid until')"
                             :value="inputs.document_expired_date.value"
                             :error="inputs.document_expired_date.error"
                             @input="setInputValue(['document_expired_date', $event])"
                             class="_green _white"/>
                </div>
              </div>
            </template>
          </div>
        </div>
        <div class="step-information__section _small">
          <span class="step-information__label">
            {{ $tInteraction('Previous national and Schengen visas obtained in the last 5 years') }}
          </span>
          <div class="step-information__col-12">
            <div class="step-information__row">
              <div class="step-information__col-12">
                <ButtonCheckList :value="inputs.have_previous_visa.value"
                                 :error="inputs.have_previous_visa.error"
                                 :options="yesOrNo"
                                 type="radio"
                                 compareType="id"
                                 text-key="name"
                                 @input="onHavePreviousVisaInput"
                />
              </div>
            </div>
            <div v-if="showHavePreviousVisa"
                 class="multiple">
              <div v-for="(visa, index) in inputs.previous_visa"
                   :key="visa._id"
                   class="multiple__item  step-information__flex">
                <button v-if="inputs.previous_visa.length > 1"
                        @click.prevent="deletePreviousVisa(index)"
                        class="multiple__delete  reset-button">
                  <BaseSvg name="ico-trash"/>
                </button>
                <div class="step-information__col-6">
                  <DatePiker :label="$tInteraction('Visa start date')"
                             :value="visa.creation_date.value"
                             @input="setArrayInputValue(['previous_visa', index, 'creation_date', $event])"
                             class="_green _white"
                  />
                </div>
                <div class="step-information__col-6">
                  <DatePiker :label="$tInteraction('Visa expiration date')"
                             :value="visa.expired_date.value"
                             @input="setArrayInputValue(['previous_visa', index, 'expired_date', $event])"
                             class="_green _white"
                  />
                </div>
              </div>
              <button v-if="showHavePreviousVisa"
                      @click="addPreviousVisa"
                      class="multiple__add  base-btn  base-btn--outline-dark  base-btn--small">
                <BaseSvg name="ico-plus"
                         width="1.2rem"
                         height="1.2rem"/>
                <span>{{ $tInteraction('Add visa') }}</span>
              </button>
            </div>
          </div>
        </div>
        <div class="step-information__section _small">
          <span class="step-information__label">
            {{ $tInteraction('Fingerprints taken earlier for a Schengen visa') }}
          </span>
          <div class="step-information__col-12">
            <div class="step-information__row">
              <div class="step-information__col-12">
                <ButtonCheckList :value="inputs.fingerprints_from_visa.value"
                                 :error="inputs.fingerprints_from_visa.error"
                                 :options="yesOrNo"
                                 @input="onFingerprintsFromVisaInput"
                                 type="radio"
                                 compareType="id"
                                 text-key="name"
                />
              </div>
            </div>
            <div v-if="showFingerprintsFromVisa"
                 class="step-information__row">
              <div class="step-information__col-6">
                <DatePiker :label="$tInteraction('Date (if known)')"
                           :value="inputs.fingerprints_from_visa_date.value"
                           :error="inputs.fingerprints_from_visa_date.error"
                           @input="setInputValue(['fingerprints_from_visa_date', $event])"
                           class="_green"
                />
              </div>
            </div>
          </div>
        </div>
        <div class="step-information__section _small">

          <div class="step-information__col-12">
            <div class="step-information__row">
              <div class="step-information__col-12">
                <span class="step-information__label">
                  {{ $tInteraction('Are you related to EU citizens') }}?
                </span>
                <ButtonCheckList :value="inputs.relative_in_es.value"
                                 :error="inputs.relative_in_es.error"
                                 :options="yesOrNo"
                                 type="radio"
                                 compareType="id"
                                 text-key="name"
                                 @input="onRelativeInEsInput"
                />
              </div>
            </div>
            <template v-if="showRelativeInEsInput">
              <div v-for="(relative, index) in inputs.relatives_in_es"
                   :key="relative._id"
                   class="step-information__card">
                <button v-if="inputs.relatives_in_es.length > 1"
                        @click.prevent="deleteRelative(index)"
                        class="multiple__delete  reset-button">
                  <BaseSvg name="ico-trash"/>
                </button>
                <div class="step-information__flex">
                  <div class="step-information__col-6">
                    <BaseInput :label="$tInteraction('Surname')"
                               :value="relative.name.value"
                               :error="relative.name.error"
                               @input="setArrayInputValue(['relatives_in_es', index, 'name', $event])"
                               class="_white"/>
                  </div>
                  <div class="step-information__col-6">
                    <BaseInput :label="$tInteraction('Name')"
                               :value="relative.surname.value"
                               :error="relative.surname.error"
                               @input="setArrayInputValue(['relatives_in_es', index, 'surname', $event])"
                               class="_white"/>
                  </div>
                </div>
                <BirthdaySelect :value="relative.birthday.value"
                                :error="relative.birthday.error"
                                labels
                                @input="setArrayInputValue(['relatives_in_es', index, 'birthday', $event])"
                                class="_white"/>

                <div class="step-information__card-line"></div>

                <span class="step-information__label">
                  {{ $tInteraction('Citizenship') }}
                </span>
                <div class="step-information__row">
                  <div class="step-information__col-12">
                    <ButtonCheckList :value="relative.has_citizenship.value"
                                     :error="relative.has_citizenship.error"
                                     :options="yesOrNo"
                                     type="radio"
                                     compareType="id"
                                     text-key="name"
                                     @input="setArrayInputValue(['relatives_in_es', index, 'has_citizenship', $event])"
                    />
                  </div>
                </div>
                <template v-if="relative.has_citizenship.value && relative.has_citizenship.value.id">
                  <div class="step-information__row">
                    <SCountry :label="$tInteraction('Specify country')"
                              :value="relative.country.value"
                              :error="relative.country.error"
                              @input="setArrayInputValue(['relatives_in_es', index, 'country', $event])"
                              class="step-information__select _white"/>
                  </div>
                  <div class="step-information__row">
                    <BaseSelect :label="$tInteraction('Relation degree')"
                                :value="relative.relative_level.value"
                                :error="relative.relative_level.error"
                                :options="relativeLevels"
                                text-key="name"
                                @input="setArrayInputValue(['relatives_in_es', index, 'relative_level', $event])"
                                class="step-information__select _white"
                    />
                  </div>

                </template>
              </div>
              <button
                  @click="addRelative"
                  class="step-information__add-button  base-btn  base-btn--outline-grey  base-btn--small">
                <BaseSvg name="ico-plus"
                         width="1.2rem"
                         height="1.2rem"/>
                <span>{{ $tInteraction('Add a relative') }}</span>
              </button>
            </template>
          </div>
        </div>
      </div>
      <BaseAlert :value="error" class="step-information__error  _error"/>
    </template>
  </InteractionStep>
</template>

<script>
  import {mapState, mapGetters, mapMutations, mapActions} from 'vuex'
  import InteractionStep from '../InteractionStep'
  import ButtonCheckList from '../../ButtonCheckList'
  import BirthdaySelect from '../../BirthdaySelect'
  import DatePiker from '../../DatePiker'
  import SCountry from '../../loadingSelects/SCountry'
  import {http} from "../../../axios"
  import interactionStepMixin from "@/mixins/interactionStepMixin"

  export default {
    name: 'InteractionStepPassport',
    mixins: [interactionStepMixin],
    components: {
      InteractionStep,
      ButtonCheckList,
      BirthdaySelect,
      DatePiker,
      SCountry,
    },
    data: () => ({
      pendingSave: false,
      error: null,
      showMaritalStatusDescription: false,
      showDocumentLiveInOtherCountry: false,
      showHavePreviousVisa: false,
      showFingerprintsFromVisa: false,
      showRelativeInEsInput: false,
    }),
    computed: {
      ...mapState('interaction', {
        yesOrNo(state) {
          return state.yesOrNo.map(item => {
            const a = {
              ...item
            }

            a.name = this.$tInteraction(a.name)

            return a
          })
        }
      }),
      ...mapState('interaction', [
        'maritalStatuses',
        'documents',
        'relativeLevels',
      ]),
      ...mapState('interaction/visa', [
        'inputs',
      ]),
      ...mapGetters('interaction/visa', [
        'formData'
      ])
    },
    methods: {
      ...mapMutations('interaction/visa', [
        'setInputValue',
        'setArrayInputValue',
        'addPreviousVisa',
        'deletePreviousVisa',
        'addRelative',
        'deleteRelative',
        'cleanInputs',
      ]),
      ...mapActions('interaction/visa', [
        'setDefaultInputs',
      ]),
      onMaritalStatusInput(value) {
        this.setInputValue(['marital_status', value])

        if (value.need_action) {
          this.showMaritalStatusDescription = true
        } else {
          this.showMaritalStatusDescription = false
          this.setInputValue(['marital_status_description', null])
        }
      },
      onDocumentLiveInOtherCountryInput(value) {
        this.setInputValue(['document_live_in_other_country', value])

        this.showDocumentLiveInOtherCountry = value.id
      },
      onHavePreviousVisaInput(value) {
        this.setInputValue(['have_previous_visa', value])
        this.showHavePreviousVisa = value.id

        if (value.id && !this.inputs.previous_visa.length) {
          this.addPreviousVisa()
        }
      },
      onFingerprintsFromVisaInput(value) {
        this.setInputValue(['fingerprints_from_visa', value])
        this.showFingerprintsFromVisa = value.id
      },
      onRelativeInEsInput(value) {
        this.setInputValue(['relative_in_es', value])
        this.showRelativeInEsInput = value.id

        if (value.id && !this.inputs.relatives_in_es.length) {
          this.addRelative()
        }
      },
      onSave() {
        this.error = null
        this.pendingSave = true
        this.cleanInputs()
        const step = this.$store.state.interaction.step

        http.post(`/api/new-interaction/${this.$route.params.id}/${step}`, this.formData).then(async () => {
          await this.$store.dispatch('interaction/loadInteraction')
        }).catch(e => {
          this.handleInteractionError(e)
        }).finally(() => {
          this.pendingSave = false
        })
      }
    },
    created() {
      this.setDefaultInputs()
    }
  }
</script>

<style>

</style>
